import { z } from 'zod';

// ─────────────────────────────────────────────────────────────────────────────
// Event taxonomy modelled on the CDVI ATRIUM A22K manual:
//   - Access (card / pin / manual / REX) granted/denied with reason codes
//   - Door physical events (forced, held-open, lockdown, REX)
//   - Reader / keypad / OSDP-2 status & tamper
//   - Card & cardholder lifecycle (incl. on-device enrollment mode)
//   - Schedule & holiday transitions
//   - Anti-passback violations
//   - Zone inputs with wire-cut / wire-short trouble (per A22K spec)
//   - System: AC mains, battery, box & wall tamper
//   - Controller heartbeat, firmware, factory reset
//   - Elevator (A22KEC) floor access
//   - Audit: config changes
// ─────────────────────────────────────────────────────────────────────────────

export const eventKinds = [
  // Access
  'access_granted',
  'access_denied',
  'rex_pressed',
  // Door physical
  'door_forced',
  'door_held_open',
  'door_unlocked_manual',
  'door_locked_manual',
  'door_lockdown_engaged',
  'door_lockdown_released',
  'door_relocked',
  // Reader
  'reader_online',
  'reader_offline',
  'reader_tamper',
  // Card / cardholder
  'card_added',
  'card_removed',
  'card_enrolled', // on-device enrollment mode
  'cardholder_created',
  'cardholder_updated',
  'cardholder_deleted',
  // Schedule / holiday
  'schedule_active',
  'schedule_inactive',
  'holiday_began',
  'holiday_ended',
  // Anti-passback
  'antipassback_violation',
  'antipassback_reset',
  // Zone (alarm) inputs
  'zone_alarm',
  'zone_restored',
  'zone_trouble', // wire-cut or wire-short supervision triggered
  'zone_normal',
  'zone_bypassed',
  'zone_unbypassed',
  // Alarm partition arming
  'partition_armed',
  'partition_disarmed',
  // System (controller-level alarms)
  'ac_power_lost',
  'ac_power_restored',
  'battery_low',
  'battery_normal',
  'battery_disconnected',
  'battery_reversed',
  'box_tamper_open',
  'box_tamper_normal',
  'wall_tamper_open',
  'wall_tamper_normal',
  // Controller
  'controller_online',
  'controller_offline',
  'controller_synced',
  'firmware_updated',
  'factory_reset',
  // Elevator
  'floor_access_granted',
  'floor_access_denied',
  // Audit
  'config_change',
] as const;

export const EventKind = z.enum(eventKinds);
export type EventKind = z.infer<typeof EventKind>;

export const SeverityLevel = z.enum(['info', 'warn', 'critical']);
export type SeverityLevel = z.infer<typeof SeverityLevel>;

export const AccessMethod = z.enum(['card', 'pin', 'card_and_pin', 'rex', 'manual']);
export const DenyReason = z.enum([
  'unknown_card',
  'expired',
  'wrong_schedule',
  'wrong_access_level',
  'antipassback',
  'door_lockdown',
  'card_disabled',
  'holiday_block',
  'wrong_pin',
  'pin_required',
  'unknown',
]);

const baseEvent = z.object({
  id: z.guid().optional(),
  controllerId: z.guid(),
  occurredAt: z.iso.datetime(),
  severity: SeverityLevel.default('info'),
  doorId: z.guid().nullable().optional(),
  readerId: z.guid().nullable().optional(),
  cardholderId: z.guid().nullable().optional(),
  cardId: z.guid().nullable().optional(),
});

// ── Access ──────────────────────────────────────────────────────────────────
export const AccessGrantedEvent = baseEvent.extend({
  kind: z.literal('access_granted'),
  payload: z.object({
    method: AccessMethod,
    direction: z.enum(['entry', 'exit']).optional(),
  }),
});

export const AccessDeniedEvent = baseEvent.extend({
  kind: z.literal('access_denied'),
  severity: z.literal('warn').default('warn'),
  payload: z.object({
    method: AccessMethod,
    reason: DenyReason,
    direction: z.enum(['entry', 'exit']).optional(),
  }),
});

export const RexPressedEvent = baseEvent.extend({
  kind: z.literal('rex_pressed'),
  payload: z.object({
    granted: z.boolean(),
  }),
});

// ── Door physical ───────────────────────────────────────────────────────────
export const DoorForcedEvent = baseEvent.extend({
  kind: z.literal('door_forced'),
  severity: z.literal('critical').default('critical'),
  payload: z.object({}).default({}),
});

export const DoorHeldOpenEvent = baseEvent.extend({
  kind: z.literal('door_held_open'),
  severity: z.literal('warn').default('warn'),
  payload: z.object({ heldForSeconds: z.number().int().nonnegative() }),
});

export const DoorManualEvent = baseEvent.extend({
  kind: z.enum(['door_unlocked_manual', 'door_locked_manual', 'door_relocked']),
  payload: z.object({
    byAppUserId: z.string().nullable().optional(),
    durationSeconds: z.number().int().nonnegative().optional(),
  }),
});

export const DoorLockdownEvent = baseEvent.extend({
  kind: z.enum(['door_lockdown_engaged', 'door_lockdown_released']),
  severity: z.literal('warn').default('warn'),
  payload: z.object({ byAppUserId: z.string().nullable().optional() }),
});

// ── Reader ──────────────────────────────────────────────────────────────────
export const ReaderStatusEvent = baseEvent.extend({
  kind: z.enum(['reader_online', 'reader_offline']),
  payload: z.object({
    technology: z.enum(['wiegand', 'osdp', 'krypto']).optional(),
  }),
});

export const ReaderTamperEvent = baseEvent.extend({
  kind: z.literal('reader_tamper'),
  severity: z.literal('critical').default('critical'),
  payload: z.object({}).default({}),
});

// ── Card / cardholder ───────────────────────────────────────────────────────
export const CardChangeEvent = baseEvent.extend({
  kind: z.enum(['card_added', 'card_removed', 'card_enrolled']),
  payload: z.object({
    cardNumber: z.string(),
    facilityCode: z.string().optional(),
    format: z.enum(['wiegand_26', 'wiegand_30', 'wiegand_44', 'mifare_classic', 'desfire_ev2']).optional(),
  }),
});

export const CardholderLifecycleEvent = baseEvent.extend({
  kind: z.enum(['cardholder_created', 'cardholder_updated', 'cardholder_deleted']),
  payload: z.object({
    name: z.string(),
    byAppUserId: z.string().nullable().optional(),
  }),
});

// ── Schedule / holiday ──────────────────────────────────────────────────────
export const ScheduleEvent = baseEvent.extend({
  kind: z.enum(['schedule_active', 'schedule_inactive']),
  payload: z.object({ scheduleId: z.string(), scheduleName: z.string().optional() }),
});

export const HolidayEvent = baseEvent.extend({
  kind: z.enum(['holiday_began', 'holiday_ended']),
  payload: z.object({ holidayId: z.string(), holidayName: z.string().optional() }),
});

// ── Anti-passback ───────────────────────────────────────────────────────────
export const AntipassbackEvent = baseEvent.extend({
  kind: z.enum(['antipassback_violation', 'antipassback_reset']),
  severity: z.literal('warn').default('warn'),
  payload: z.object({ areaId: z.string().optional() }),
});

// ── Zone inputs ─────────────────────────────────────────────────────────────
export const ZoneEvent = baseEvent.extend({
  kind: z.enum(['zone_alarm', 'zone_restored']),
  severity: z.literal('critical').default('critical'),
  payload: z.object({
    zoneId: z.string(),
    zoneName: z.string().optional(),
  }),
});

export const ZoneTroubleEvent = baseEvent.extend({
  kind: z.enum(['zone_trouble', 'zone_normal']),
  severity: z.literal('warn').default('warn'),
  payload: z.object({
    zoneId: z.string(),
    troubleType: z.enum(['wire_cut', 'wire_short', 'unknown']).optional(),
  }),
});

export const ZoneBypassEvent = baseEvent.extend({
  kind: z.enum(['zone_bypassed', 'zone_unbypassed']),
  payload: z.object({
    zoneId: z.string(),
    byAppUserId: z.string().nullable().optional(),
  }),
});

// ── Partition arming ────────────────────────────────────────────────────────
export const PartitionEvent = baseEvent.extend({
  kind: z.enum(['partition_armed', 'partition_disarmed']),
  payload: z.object({
    partitionId: z.string(),
    mode: z.enum(['away', 'stay', 'instant']).optional(),
    byAppUserId: z.string().nullable().optional(),
  }),
});

// ── System (controller-level) ───────────────────────────────────────────────
export const SystemAlarmEvent = baseEvent.extend({
  kind: z.enum([
    'ac_power_lost',
    'ac_power_restored',
    'battery_low',
    'battery_normal',
    'battery_disconnected',
    'battery_reversed',
    'box_tamper_open',
    'box_tamper_normal',
    'wall_tamper_open',
    'wall_tamper_normal',
  ]),
  severity: z.enum(['info', 'warn', 'critical']).default('warn'),
  payload: z
    .object({
      voltage: z.number().optional(),
      temperatureC: z.number().optional(),
    })
    .default({}),
});

// ── Controller lifecycle ────────────────────────────────────────────────────
export const ControllerStatusEvent = baseEvent.extend({
  kind: z.enum(['controller_online', 'controller_offline', 'controller_synced']),
  payload: z
    .object({
      firmwareVersion: z.string().optional(),
      uptimeSeconds: z.number().int().nonnegative().optional(),
    })
    .default({}),
});

export const FirmwareUpdatedEvent = baseEvent.extend({
  kind: z.literal('firmware_updated'),
  payload: z.object({ from: z.string(), to: z.string() }),
});

export const FactoryResetEvent = baseEvent.extend({
  kind: z.literal('factory_reset'),
  severity: z.literal('critical').default('critical'),
  payload: z.object({ byAppUserId: z.string().nullable().optional() }).default({}),
});

// ── Elevator (A22KEC) ───────────────────────────────────────────────────────
export const FloorAccessEvent = baseEvent.extend({
  kind: z.enum(['floor_access_granted', 'floor_access_denied']),
  payload: z.object({
    floorNumber: z.number().int().min(0).max(256),
    method: AccessMethod,
    reason: DenyReason.optional(),
  }),
});

// ── Audit ───────────────────────────────────────────────────────────────────
export const ConfigChangeEvent = baseEvent.extend({
  kind: z.literal('config_change'),
  payload: z.object({
    target: z.string(),
    field: z.string().optional(),
    byAppUserId: z.string().nullable().optional(),
  }),
});

// ── Discriminated union ─────────────────────────────────────────────────────
export const AtriumEvent = z.discriminatedUnion('kind', [
  AccessGrantedEvent,
  AccessDeniedEvent,
  RexPressedEvent,
  DoorForcedEvent,
  DoorHeldOpenEvent,
  DoorManualEvent,
  DoorLockdownEvent,
  ReaderStatusEvent,
  ReaderTamperEvent,
  CardChangeEvent,
  CardholderLifecycleEvent,
  ScheduleEvent,
  HolidayEvent,
  AntipassbackEvent,
  ZoneEvent,
  ZoneTroubleEvent,
  ZoneBypassEvent,
  PartitionEvent,
  SystemAlarmEvent,
  ControllerStatusEvent,
  FirmwareUpdatedEvent,
  FactoryResetEvent,
  FloorAccessEvent,
  ConfigChangeEvent,
]);
export type AtriumEvent = z.infer<typeof AtriumEvent>;

export const RelayEventBatch = z.object({
  controllerId: z.guid(),
  events: z.array(AtriumEvent).max(500),
});
export type RelayEventBatch = z.infer<typeof RelayEventBatch>;

export const CRITICAL_EVENT_KINDS = new Set<EventKind>([
  'door_forced',
  'reader_tamper',
  'zone_alarm',
  'box_tamper_open',
  'wall_tamper_open',
  'controller_offline',
  'factory_reset',
]);

export const eventLabels: Record<EventKind, string> = {
  access_granted: 'Access granted',
  access_denied: 'Access denied',
  rex_pressed: 'Request to exit',
  door_forced: 'Door forced open',
  door_held_open: 'Door held open',
  door_unlocked_manual: 'Door unlocked',
  door_locked_manual: 'Door locked',
  door_lockdown_engaged: 'Lockdown engaged',
  door_lockdown_released: 'Lockdown released',
  door_relocked: 'Door re-locked',
  reader_online: 'Reader online',
  reader_offline: 'Reader offline',
  reader_tamper: 'Reader tamper',
  card_added: 'Card added',
  card_removed: 'Card removed',
  card_enrolled: 'Card enrolled',
  cardholder_created: 'Cardholder created',
  cardholder_updated: 'Cardholder updated',
  cardholder_deleted: 'Cardholder deleted',
  schedule_active: 'Schedule active',
  schedule_inactive: 'Schedule inactive',
  holiday_began: 'Holiday began',
  holiday_ended: 'Holiday ended',
  antipassback_violation: 'Anti-passback violation',
  antipassback_reset: 'Anti-passback reset',
  zone_alarm: 'Zone alarm',
  zone_restored: 'Zone restored',
  zone_trouble: 'Zone trouble',
  zone_normal: 'Zone normal',
  zone_bypassed: 'Zone bypassed',
  zone_unbypassed: 'Zone un-bypassed',
  partition_armed: 'Partition armed',
  partition_disarmed: 'Partition disarmed',
  ac_power_lost: 'AC power lost',
  ac_power_restored: 'AC power restored',
  battery_low: 'Battery low',
  battery_normal: 'Battery normal',
  battery_disconnected: 'Battery disconnected',
  battery_reversed: 'Battery reversed',
  box_tamper_open: 'Box tamper open',
  box_tamper_normal: 'Box tamper closed',
  wall_tamper_open: 'Wall tamper open',
  wall_tamper_normal: 'Wall tamper closed',
  controller_online: 'Controller online',
  controller_offline: 'Controller offline',
  controller_synced: 'Controller synced',
  firmware_updated: 'Firmware updated',
  factory_reset: 'Factory reset',
  floor_access_granted: 'Floor access granted',
  floor_access_denied: 'Floor access denied',
  config_change: 'Configuration change',
};
